import { useStyletron } from '@thanx/uikit/theme'
import React, { useCallback, useEffect, useRef } from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface SelectProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether the dropdown menu is open */
  isOpen?: boolean
  /** Whether the select is disabled */
  disabled?: boolean
  /** Called when user clicks outside or presses Escape (to close the dropdown) */
  onClose?: () => void
  children: React.ReactNode
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled Select component.
 *
 * Composable wrapper for SelectTrigger, SelectMenu, and SelectOption.
 * Does NOT manage open/close state - parent controls via isOpen prop.
 * All visual state determined by props. Spreads ...props for HOC injection.
 *
 * Calls onClose when:
 * - User clicks outside the select
 * - User presses Escape key
 *
 * @example
 * <Select isOpen={isOpen} onClose={() => setIsOpen(false)}>
 *   <SelectTrigger onToggle={toggleOpen} showClear onClear={handleClear}>
 *     {selectedLabel}
 *   </SelectTrigger>
 *   <SelectMenu isOpen={isOpen}>
 *     {options.map(opt => (
 *       <SelectOption
 *         key={opt.value}
 *         value={opt.value}
 *         selected={selectedValue === opt.value}
 *         onSelect={handleSelect}
 *       >
 *         {opt.label}
 *       </SelectOption>
 *     ))}
 *   </SelectMenu>
 * </Select>
 */
const Select = React.memo(
  ({
    isOpen = false,
    disabled = false,
    onClose,
    children,
    style,
    className,
    ...props
  }: SelectProps) => {
    const [css] = useStyletron()
    const containerRef = useRef<HTMLDivElement>(null)

    // Handle click outside
    const handleClickOutside = useCallback(
      (event: MouseEvent) => {
        if (
          isOpen &&
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          onClose?.()
        }
      },
      [isOpen, onClose]
    )

    // Handle escape key
    const handleKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (isOpen && event.key === 'Escape') {
          onClose?.()
        }
      },
      [isOpen, onClose]
    )

    // NOTE: useEffect is allowed here because it's for event listener cleanup,
    // not for state initialization or side effects. The component remains controlled.
    useEffect(() => {
      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleKeyDown)
        return () => {
          document.removeEventListener('mousedown', handleClickOutside)
          document.removeEventListener('keydown', handleKeyDown)
        }
      }
      return undefined
    }, [isOpen, handleClickOutside, handleKeyDown])

    const containerClassName = css(getContainerStyles())

    return (
      <div
        ref={containerRef}
        data-open={isOpen}
        data-disabled={disabled}
        className={`${containerClassName} ${className || ''}`}
        style={style}
        {...props}
      >
        {children}
      </div>
    )
  }
)

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

const getContainerStyles = () => ({
  position: 'relative' as const,
})

export default Select
